import { lazy, Suspense } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, type LoginInput } from "@/lib/validations/auth";
import { supabase, getSiteUrl } from "@/lib/supabase";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

const LOGO_URL =
  "https://dtyaaijttydrfjmxksat.supabase.co/storage/v1/object/public/public/krak%20icon.png?t=2024-12-13T05%3A57%3A20.033Z";

const LoginForm = () => {
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const onSubmit = async (data: LoginInput) => {
    try {
      await signIn(data.email, data.password);
      navigate("/");
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Error",
        description: "Failed to sign in. Please check your credentials.",
        variant: "destructive",
      });
    } finally {
      form.setValue("password", "");
    }
  };

  const handleContinueAsGuest = () => {
    navigate("/");
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-[400px] max-w-full bg-black/30 backdrop-blur-xl rounded-[32px] shadow-xl overflow-hidden border border-white/20 p-8 space-y-4"
      >
        {/* <div className="text-center space-y-4 mb-4">
          <img
            src={LOGO_URL}
            alt="Krak Logo"
            className="mx-auto w-24 h-24 rounded-xl"
            loading="eager"
          />
          <h2 className="text-xl font-semibold text-white drop-shadow-sm">
            Welcome Back
          </h2>
        </div> */}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Email"
                  {...field}
                  autoComplete="email"
                  className="h-12 rounded-xl border-white/20 bg-black/20 text-white placeholder:text-white/60 focus:bg-black/30 transition-colors"
                />
              </FormControl>
              <FormMessage className="text-red-200 drop-shadow-sm" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Password"
                  {...field}
                  autoComplete="current-password"
                  className="h-12 rounded-xl border-white/20 bg-black/20 text-white placeholder:text-white/60 focus:bg-black/30 transition-colors"
                />
              </FormControl>
              <FormMessage className="text-red-200 drop-shadow-sm" />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="w-full h-12 bg-gradient-to-r from-[#735fe2] to-[#627ae2] hover:opacity-90 transition-opacity rounded-xl text-base font-medium text-white shadow-lg"
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? "Signing in..." : "Sign In"}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-white/20" />
          </div>
          <div className="relative flex justify-center text-xs uppercase"></div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full h-12 border-white/20 bg-black/20 text-white hover:bg-black/30 transition-colors rounded-xl flex items-center justify-center gap-2"
          onClick={async () => {
            try {
              const { data, error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                  redirectTo: getSiteUrl(), // Use the helper function to get the correct URL
                },
              });
              if (error) throw error;
            } catch (error) {
              console.error("Error signing in with Google:", error);
              toast({
                title: "Error",
                description: "Failed to sign in with Google",
                variant: "destructive",
              });
            }
          }}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full h-12 border-white/20 bg-black/20 text-white hover:bg-black/30 transition-colors rounded-xl"
          onClick={handleContinueAsGuest}
        >
          Continue as <span className="font-semibold ml-1">Guest</span>
        </Button>

        <div className="text-center space-y-2">
          <Link
            to="/reset-password"
            className="text-sm text-white/90 hover:text-white block drop-shadow-sm transition-colors"
          >
            Forgot password?
          </Link>
          <p className="text-sm text-white/90 drop-shadow-sm">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="text-white font-medium hover:text-white/90 transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>
      </form>
    </Form>
  );
};

const Login = () => {
  return <LoginForm />;
};

export default Login;
